import { NetPingDeviceFindDto, type NetPingDeviceFindInterface } from '@zaoblako/shared/modules/pacs/dtos/net-ping-device/net-ping-device-find.dto';
import { ConverterFindDto, type ConverterFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { ExternalUserFindDto, type ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ClientFindDto, type ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import { FileFindDto, type FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { ContractFindDto, type ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import { RoomStatusEnum } from '@zaoblako/shared/modules/catalog/enums/room-status.enum';
import { RoomTypeEnum } from '@zaoblako/shared/modules/catalog/enums/room-type.enum';

import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';

import * as yup from 'yup';

/** Room response body */
export interface RoomFindInterface {
  /** Unique identifier. Cannot be changed */
  id: number;
  /** Display name. */
  name: string;
  /** Room area in square meters */
  square: number;
  /** Room floor number */
  floor?: number;
  /** Number of parking places */
  parkingPlacesCount?: number;
  /** Room type */
  type?: RoomTypeEnum;
  /** Room status */
  status: RoomStatusEnum;
  /** Finish condition description */
  finishCondition?: string;
  /** Room plan file */
  roomPlan?: FileFindInterface;
  /** Floor plan file */
  floorPlan?: FileFindInterface;
  /** Related filial */
  filial?: FilialFindInterface;
  /** "NetPing" devices controlling access to this room */
  netPingDevices?: NetPingDeviceFindInterface[];
  /** Room entrance converters */
  converters?: ConverterFindInterface[];
  /** Related external users */
  externalUsers?: ExternalUserFindInterface[];
  /** Related client */
  client?: ClientFindInterface;
  /** Related users who have access to the premises */
  allowUsers?: UserFindInterface[];
  /** Mean rental rate (₽/m²) over qualifying deals */
  averageRentRate?: number;
  /** Mean monthly rent over qualifying deals */
  averageMonthlyPayment?: number;
  /** Mean corridor factor over qualifying deals */
  averageCorridorFactor?: number;
  /** Weighted mean rental rate by room type (benchmark) */
  averageRateByType?: number;
  /** (averageRentRate − averageRateByType) / averageRateByType × 100 */
  deviationFromNormPercent?: number;
  /** Active contracts for the room (deal aggregates) */
  activeContracts?: ContractFindInterface[];
  /** Last room update time (entity field); always present on persisted room */
  updatedAt?: Date;
}

/** Room response body - DTO schema */
export const RoomFindDto = SerdeDtoClass.create<RoomFindInterface>(CatalogDtosKeysEnum.ROOM_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  square: {
    optional: true,
    sortable: true,
    label: 'Площадь',
    serializer: yup.number(),
  },
  floor: {
    optional: true,
    sortable: true,
    label: 'Этаж',
    serializer: yup.number(),
  },
  parkingPlacesCount: {
    optional: true,
    sortable: true,
    label: 'Кол-во парковочных мест',
    serializer: yup.number(),
  },
  type: {
    optional: true,
    sortable: true,
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(RoomTypeEnum)),
  },
  status: {
    sortable: true,
    label: 'Статус',
    serializer: yup
      .string()
      .oneOf(Object.values(RoomStatusEnum)),
  },
  finishCondition: {
    optional: true,
    label: 'Состояние отделки',
    serializer: yup.string(),
  },
  roomPlan: {
    optional: true,
    label: 'План помещения',
    nested: FileFindDto,
    serializer: (_, nested) => nested,
  },
  floorPlan: {
    optional: true,
    label: 'Расположение на плане этажа',
    nested: FileFindDto,
    serializer: (_, nested) => nested,
  },
  filial: {
    optional: true,
    sortable: true,
    label: 'Филиал',
    nested: CatalogDtosKeysEnum.FILIAL_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  client: {
    optional: true,
    sortable: true,
    label: 'Клиент',
    nested: ClientFindDto,
    serializer: (_, nested) => nested,
  },
  netPingDevices: {
    optional: true,
    label: 'Устройства "netPing"',
    nested: NetPingDeviceFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  converters: {
    optional: true,
    label: 'Конвертеры',
    nested: ConverterFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  externalUsers: {
    optional: true,
    label: 'Контакты',
    nested: ExternalUserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  allowUsers: {
    optional: true,
    label: 'Пользователи, имеющие доступ к помещению',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  averageRentRate: {
    optional: true,
    label: 'Арендная ставка (среднее)',
    serializer: yup.number(),
  },
  averageMonthlyPayment: {
    optional: true,
    label: 'Месячный платёж (среднее)',
    serializer: yup.number(),
  },
  averageCorridorFactor: {
    optional: true,
    label: 'Коридорный коэффициент (среднее)',
    serializer: yup.number(),
  },
  averageRateByType: {
    optional: true,
    label: 'Средняя ставка по типу',
    serializer: yup.number(),
  },
  deviationFromNormPercent: {
    optional: true,
    label: 'Отклонение от нормы, %',
    serializer: yup.number(),
  },
  activeContracts: {
    optional: true,
    label: 'Договоры',
    nested: ContractFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  updatedAt: {
    optional: true,
    label: 'Обновлено',
    serializer: yup.date(),
  },
});
